import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Flow,
  GStats,
  HBars,
  StatTile,
} from '@/components/ui';
import {
  useDispensaryDashboard,
  useDispensingQueue,
  useFinanceDashboard,
  useIntegrations,
  useStaff,
  useStockOverview,
  useWellbeingAccess,
  useWellbeingDashboard,
} from '@/features/admin-v2/api';
import {
  claimNumber,
  claimSla,
  claimStatus,
} from '@/features/admin-v2/claim-status';
import type { AuditEvent } from '@/features/audit/api';
import { integrationStatus } from '@/pages/ops/integration-status';
import { MFA_REQUIRED_ROLES } from '@/features/auth/roles';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useApprovals, useFeeServices } from '@/features/billing/api';
import { formatRM } from '@/features/billing/money';
import { api, type PageMeta } from '@/lib/api';
import { klTime, todayKl } from '@/pages/calendar/time';
import styles from './dashboards.module.css';

const scope = (name: string | undefined) => name ?? 'All branches';

/** Finance dashboard (prototype finance: tiles, claims pipeline, deadlines, outstanding by panel). */
export function FinanceDashboard() {
  const { selected } = useBranch();
  const d = useFinanceDashboard().data;
  const t = d?.tiles;
  const stage = (s: string) => d?.pipeline.find((p) => p.status === s);
  const steps = ['draft', 'ready', 'submitted', 'queried', 'paid'].map((s) => {
    const p = stage(s);
    return {
      label: claimStatus(s).label,
      value: p?.count ?? 0,
      caption: formatRM(p?.sen ?? 0),
      hot: s === 'queried' && !!p?.count,
      zero: !p?.count,
    };
  });
  const outstanding = d?.outstandingByPanel ?? [];
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="Claims due ≤ 24 h"
          value={t?.claimsDue24h ?? '—'}
          caption="Within the panel window"
        />
        <StatTile
          label="Queried"
          value={t?.queried ?? '—'}
          caption="Need a reply"
        />
        <StatTile
          label="Outstanding"
          value={t ? formatRM(t.outstandingSen) : '—'}
          caption="Issued invoices not yet paid"
        />
        <StatTile
          label="Unapplied remittance"
          value={t ? formatRM(t.unappliedRemittanceSen) : '—'}
          caption={t ? `${t.unappliedCount} awaiting knock-off` : undefined}
        />
      </div>
      <Card
        title="Claims pipeline"
        flush
        actions={<span className={styles.muted}>{scope(selected?.name)}</span>}
      >
        <Flow steps={steps} />
      </Card>
      <div className={styles.halves}>
        <Card
          title="Claims by deadline"
          flush
          actions={
            <Link to="/finance/claims">
              <Button size="sm">All claims</Button>
            </Link>
          }
        >
          {d?.byDeadline.length ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Claim</th>
                  <th>Panel</th>
                  <th>Amount</th>
                  <th>SLA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.byDeadline.map((c) => {
                  const sla = claimSla(c);
                  const st = claimStatus(c.status);
                  return (
                    <tr key={c.id}>
                      <td className="mono">{claimNumber(c)}</td>
                      <td>{c.payer.name}</td>
                      <td>{formatRM(c.amountSen)}</td>
                      <td className={sla?.late ? styles.late : undefined}>
                        {sla?.text ?? '-'}
                      </td>
                      <td>
                        <Badge tone={st.tone}>{st.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <EmptyState
              title="No claims waiting"
              body="Open claims with a panel deadline appear here."
            />
          )}
        </Card>
        <Card
          title="Outstanding by panel"
          actions={
            <Link to="/finance/ageing">
              <Button size="sm">Ageing</Button>
            </Link>
          }
        >
          {outstanding.length ? (
            <HBars
              rows={outstanding.map((o) => ({
                label: o.payer,
                value: o.sen,
                display: formatRM(o.sen),
                tone: 'cur' as const,
              }))}
              total={{
                label: 'Total',
                display: formatRM(outstanding.reduce((s, o) => s + o.sen, 0)),
              }}
            />
          ) : (
            <EmptyState
              title="Nothing outstanding"
              body="Issued invoices awaiting payment appear here."
            />
          )}
        </Card>
      </div>
    </div>
  );
}

/** Dispenser dashboard (prototype: tiles, To dispense, Stock watch). */
export function DispenserDashboard() {
  const { selected } = useBranch();
  const d = useDispensaryDashboard().data;
  const queue = useDispensingQueue().data ?? [];
  const stock = useStockOverview().data ?? [];
  const watch = stock.filter((s) => s.status !== 'ok').slice(0, 6);
  if (!selected)
    return (
      <p className={styles.warnNote}>
        Choose the branch you are working at in the branch switch.
      </p>
    );
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="Signed, to dispense"
          value={d?.toDispense ?? '—'}
          caption="Oldest signed first"
        />
        <StatTile label="Dispensed today" value={d?.dispensedToday ?? '—'} />
        <StatTile
          label="Out of stock"
          value={d?.outOfStock ?? '—'}
          caption="Doctor told on the prescription"
        />
        <StatTile
          label="Deliveries"
          value={d?.deliveries ?? '—'}
          caption="Packed for delivery"
        />
      </div>
      <Card
        title="To dispense"
        flush
        actions={
          <Link to="/dispensary/prescriptions">
            <Button size="sm">Prescriptions</Button>
          </Link>
        }
      >
        {queue.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Rx</th>
                <th>Patient</th>
                <th>Items</th>
                <th>Signed by</th>
              </tr>
            </thead>
            <tbody>
              {queue.slice(0, 8).map((rx) => (
                <tr key={rx.prescriptionId}>
                  <td className="mono">{rx.number}</td>
                  <td>
                    <strong>{rx.patient.fullName}</strong>
                  </td>
                  <td>
                    {rx.items
                      .map((i) => `${i.medicine} ×${i.quantity}`)
                      .join(', ')}
                  </td>
                  <td>
                    {rx.prescriber.fullName}
                    {rx.signedAt && (
                      <span className={styles.subLine}>
                        {klTime(rx.signedAt)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title="Nothing to dispense"
            body="Signed prescriptions appear here."
          />
        )}
      </Card>
      <Card
        title="Stock watch"
        actions={
          <Link to="/stock">
            <Button size="sm">Stock & expiry</Button>
          </Link>
        }
      >
        {watch.length ? (
          <HBars
            rows={watch.map((s) => ({
              label: s.name,
              value: s.usable,
              display:
                s.daysLeft === null
                  ? `${s.usable} ${s.unit}`
                  : `${s.daysLeft} d`,
              tone: s.status === 'out' ? ('err' as const) : ('warn' as const),
              extra: (
                <Badge tone={s.status === 'out' ? 'danger' : 'warning'}>
                  {s.status === 'out' ? 'Out' : 'Reorder'}
                </Badge>
              ),
            }))}
          />
        ) : (
          <EmptyState
            title="Nothing low"
            body="Medicines at or below their reorder point appear here."
          />
        )}
      </Card>
    </div>
  );
}

/** Mental-health dashboard (prototype: tiles, Today, compartment note). */
export function WellbeingDashboard() {
  const d = useWellbeingDashboard().data;
  const access = useWellbeingAccess();
  const next = d?.today.find((s) => new Date(s.startsAt) > new Date());
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="Sessions today"
          value={d?.counts.today ?? '—'}
          caption={
            next
              ? `next ${klTime(next.startsAt)} · ${next.patient.fullName}`
              : undefined
          }
        />
        <StatTile
          label="Screening flags"
          value={d?.counts.urgent ?? '—'}
          caption="Crisis-range score or urgent slot"
        />
        <StatTile
          label="Compartment opens"
          value={access.data?.grants.length ?? '—'}
          caption="Active grants, all logged"
        />
        <StatTile
          label="No-shows this month"
          value={d?.counts.noShowsThisMonth ?? '—'}
          caption="Your sessions marked no-show"
        />
      </div>
      <Card
        title="Today"
        flush
        actions={
          <Link to="/wellbeing/sessions">
            <Button size="sm">Sessions</Button>
          </Link>
        }
      >
        {d?.today.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Patient</th>
                <th>Type</th>
                <th>Screening</th>
              </tr>
            </thead>
            <tbody>
              {d.today.map((s) => (
                <tr key={s.id}>
                  <td>{klTime(s.startsAt)}</td>
                  <td>
                    <strong>{s.patient.fullName}</strong>
                  </td>
                  <td>{s.type}</td>
                  <td>
                    {s.screening?.score != null ? (
                      <Badge tone={s.urgent ? 'danger' : 'neutral'}>
                        {`${s.screening.tool} ${s.screening.score}`}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title="No sessions today"
            body="Booked sessions for today appear here."
          />
        )}
      </Card>
      <p className={styles.mhNote}>
        This console is a separate compartment. Nothing here appears in the GP
        encounter view; panels see a service code only.
      </p>
    </div>
  );
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hm = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

interface RosterRow {
  id: string;
  doctor: string | null;
  branch: string;
  weekday: number;
  startMin: number;
  endMin: number;
}

function useTodayAudit(enabled: boolean, pageSize = 5) {
  // The clinic date is in the key, so a dashboard left open past midnight
  // starts the new day.
  const day = todayKl();
  return useQuery({
    queryKey: ['audit-events', 'today', day, pageSize],
    queryFn: ({ signal }) =>
      api.get<AuditEvent[], PageMeta>('/audit-events', {
        signal,
        // RL-1816/RL-1817: changes only, so views do not fill Recent changes.
        query: {
          from: `${day}T00:00:00+08:00`,
          changesOnly: true,
          page: 1,
          pageSize,
        },
      }),
    enabled,
  });
}

/** Clinic manager dashboard (prototype admin: tiles, Recent changes, Roster today). */
export function ManagerDashboard() {
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const rosters = useQuery({
    queryKey: ['admin', 'rosters', selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api.get<RosterRow[]>('/admin/rosters', { signal }).then((r) => r.data),
    enabled: hasPermission('rosters:read'),
  });
  const services = useFeeServices();
  const approvals = useApprovals('waiting');
  const audit = useTodayAudit(hasPermission('audit_log:read'));
  const weekday = new Date(`${todayKl()}T12:00:00+08:00`).getDay();
  const today = (rosters.data ?? []).filter((r) => r.weekday === weekday);
  const events = audit.data?.data ?? [];
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="Doctors on roster"
          value={rosters.data ? new Set(today.map((r) => r.doctor)).size : '—'}
          caption={`${DAY[weekday]} · ${scope(selected?.name)}`}
        />
        <StatTile
          label="Services"
          value={services.data?.filter((s) => s.isActive).length ?? '—'}
          caption="Active in the fee schedule"
        />
        <StatTile
          label="Approvals waiting"
          value={approvals.data?.length ?? '—'}
          caption="Refunds, voids, discounts"
        />
        <StatTile
          label="Audit events today"
          value={audit.data?.meta?.total ?? '—'}
          caption="Changes only; views stay in the log"
        />
      </div>
      <div className={styles.halves}>
        <Card
          title="Recent changes"
          flush
          actions={
            <Link to="/audit-log">
              <Button size="sm">Audit log</Button>
            </Link>
          }
        >
          {events.length ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td>{klTime(e.occurredAt)}</td>
                    <td>{e.actor?.fullName ?? 'System'}</td>
                    <td>
                      <strong>{e.label}</strong>
                      {e.branch && (
                        <span className={styles.subLine}>{e.branch.name}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No changes today" />
          )}
        </Card>
        <Card
          title={`Roster today · ${scope(selected?.name)}`}
          flush
          actions={
            <Link to="/setup/rosters">
              <Button size="sm">Rosters</Button>
            </Link>
          }
        >
          {today.length ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Doctor</th>
                  <th>Branch</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                {today.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.doctor ?? '-'}</strong>
                    </td>
                    <td>{r.branch}</td>
                    <td>{`${hm(r.startMin)}-${hm(r.endMin)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState
              title="No roster for today"
              body="Weekly rosters are set in Doctors & rosters."
            />
          )}
        </Card>
      </div>
    </div>
  );
}

interface GroupRow {
  branch: string;
  visits: number;
  revenueSen: number;
  panelOutstandingSen?: number;
  patientOutstandingSen?: number;
  panelSharePct: number | null;
  bookedPct: number | null;
}

/** Super admin dashboard (prototype super: tiles, Needs attention, Group this month). */
/**
 * RL-1793 (#153): a current assignment (no end, or ending later) to a role
 * that must use two-factor. An ended role no longer counts.
 */
function mustUseTwoFactor(
  person: { roles?: { role: string; endsAt?: string | null }[] },
  now = Date.now(),
) {
  return (person.roles ?? []).some(
    (r) =>
      (MFA_REQUIRED_ROLES as readonly string[]).includes(r.role) &&
      (!r.endsAt || Date.parse(r.endsAt) > now),
  );
}

export function SuperAdminDashboard() {
  const { hasPermission, me } = useSession();
  const staff = useStaff();
  const integrations = useIntegrations();
  const audit = useTodayAudit(hasPermission('audit_log:read'));
  const group = useQuery({
    queryKey: ['reports', 'group'],
    queryFn: ({ signal }) =>
      api
        .get<{ rows: GroupRow[] }>('/reports/group', { signal })
        .then((r) => r.data.rows),
    enabled: hasPermission('group_reports:read'),
  });
  const people = staff.data ?? [];
  const active = people.filter((p) => p.status === 'active');
  const suspended = people.filter((p) => p.status !== 'active').length;
  // RL-1793 (#153): only roles that must use two-factor need attention.
  // RL-2160: the API decides at runtime whether the rule is enforced here
  // (a Super admin's own `mfa.required`); unknown counts as enforced.
  const mfaEnforced = me?.mfa.required ?? true;
  const needMfa = mfaEnforced ? active.filter((p) => mustUseTwoFactor(p)) : [];
  const noMfa = needMfa.filter((p) => !p.mfaEnabled);
  // RL-1824: the E-mail row reports `connected`. Not connected (no
  // credentials yet) and not working (a failing provider) count separately.
  const shown = (integrations.data ?? []).map(
    (i) => integrationStatus(i.status).label,
  );
  const notConnected = shown.filter((l) => l === 'Not connected').length;
  const notWorking = shown.filter(
    (l) => l !== 'Connected' && l !== 'Not connected',
  ).length;
  const integrationWords = [
    notConnected ? `${notConnected} not connected` : '',
    notWorking ? `${notWorking} not working` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const rows = group.data ?? [];
  const visits = rows.reduce((s, r) => s + r.visits, 0);
  const outstanding = rows.reduce(
    (sum, row) =>
      sum + (row.panelOutstandingSen ?? 0) + (row.patientOutstandingSen ?? 0),
    0,
  );
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="Staff accounts"
          value={staff.data ? people.length : '—'}
          caption={
            staff.data
              ? `${suspended} not active · two-factor on ${needMfa.length - noMfa.length}/${needMfa.length} who need it`
              : undefined
          }
        />
        <StatTile
          label="Group outstanding"
          value={group.data ? formatRM(outstanding) : '—'}
          caption="Open panel and patient balances"
        />
        <StatTile
          label="Integrations"
          value={integrations.data?.length ?? '—'}
          caption={
            integrations.data ? integrationWords || 'All connected' : undefined
          }
        />
        <StatTile
          label="Audit events today"
          value={audit.data?.meta?.total ?? '—'}
          caption="Changes today; views stay in the log"
        />
      </div>
      <div className={styles.halves}>
        <Card title="Needs attention">
          {noMfa.length || notConnected || notWorking ? (
            <GStats
              rows={[
                ...(noMfa.length
                  ? [
                      {
                        label: `${noMfa.length} staff must turn on two-factor`,
                        value: (
                          <Link to="/admin/staff">
                            <Button size="sm">Staff</Button>
                          </Link>
                        ),
                        caption: noMfa
                          .map((p) => p.fullName)
                          .slice(0, 3)
                          .join(', '),
                      },
                    ]
                  : []),
                ...(notWorking
                  ? [
                      {
                        label: `${notWorking} integrations not working`,
                        value: (
                          <Link to="/admin/integrations">
                            <Button size="sm">Integrations</Button>
                          </Link>
                        ),
                        caption: 'Tell the system team',
                      },
                    ]
                  : []),
                ...(notConnected
                  ? [
                      {
                        label: `${notConnected} integrations not connected`,
                        value: (
                          <Link to="/admin/integrations">
                            <Button size="sm">Integrations</Button>
                          </Link>
                        ),
                        caption: 'Simulated until credentials are supplied',
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <EmptyState title="Nothing needs attention" />
          )}
        </Card>
        <Card title="Group this month">
          {rows.length ? (
            <HBars
              rows={rows.map((r) => ({
                label: r.branch,
                value: r.visits,
                display: `${r.visits} visits · ${formatRM(r.revenueSen)}`,
                tone: 'main' as const,
              }))}
              total={{ label: 'Visits', display: visits }}
            />
          ) : (
            <EmptyState title="No visits yet this month" />
          )}
        </Card>
      </div>
    </div>
  );
}
